// src/data/chartPrepData.js
// Epiphany MD — MA Chart Prep Checklist definitions
//
// Single source of truth for what items exist per visit type.
// The component reads from this file; the DB stores only the checked state.

export const VISIT_TYPES = [
  { id: 'new_patient',      label: 'New patient' },
  { id: 'followup',         label: 'Follow-up' },
  { id: 'postop_2wk',       label: 'Post-op (2 wk)' },
  { id: 'postop_6wk',       label: 'Post-op (6 wk)' },
  { id: 'preop',            label: 'Pre-op clearance' },
  { id: 'surgical_consult', label: 'Surgical consult' },
];

// ─── Base sections — applied to every visit type ─────────────────────────────

export const BASE_SECTIONS = [
  {
    id: 'demographics',
    label: 'Demographics & Insurance',
    items: [
      {
        id: 'demo_verified',
        label: 'Demographics confirmed with patient',
        sub: 'Name, DOB, address, phone — ask patient to confirm verbally',
      },
      {
        id: 'insurance_card',
        label: 'Insurance card scanned / on file',
        required: true,
      },
      {
        id: 'insurance_verified',
        label: 'Insurance eligibility verified',
        sub: 'Coverage active, copay amount, deductible status',
      },
      {
        id: 'copay_collected',
        label: 'Copay collected or waiver signed',
      },
      {
        id: 'photo_id',
        label: 'Photo ID scanned',
      },
      {
        id: 'consents',
        label: 'Consent forms signed and scanned',
        sub: 'HIPAA notice, financial responsibility',
      },
    ],
  },
  {
    id: 'clinical',
    label: 'Clinical Prep',
    items: [
      {
        id: 'allergies',
        label: 'Allergies reviewed and confirmed with patient',
        required: true,
        sub: 'Ask patient verbally — do not copy from last visit without confirming',
      },
      {
        id: 'medications',
        label: 'Medication list reconciled',
        sub: 'Current meds, doses, last fill — note anticoagulants and NSAIDs',
      },
      {
        id: 'pmh',
        label: 'Past medical history current',
      },
      {
        id: 'psh',
        label: 'Surgical history current',
        sub: 'Include prior ortho procedures — laterality matters',
      },
      {
        id: 'vitals',
        label: 'Vitals entered',
        sub: 'BP, HR, weight, pain scale 0–10',
      },
      {
        id: 'chief_complaint',
        label: 'Chief complaint documented',
        sub: "Patient's own words — not a diagnosis",
      },
    ],
  },
  {
    id: 'imaging',
    label: 'Records & Imaging',
    items: [
      {
        id: 'imaging_available',
        label: 'Relevant imaging available in chart',
        sub: 'X-rays, MRI, CT — confirm viewable in DICOM viewer or notes',
      },
      {
        id: 'outside_records',
        label: 'Outside records requested / received if applicable',
      },
      {
        id: 'referring_provider',
        label: 'Referring provider info documented',
        sub: 'Name, phone, fax — required for note routing',
      },
    ],
  },
];

// ─── Visit-type-specific extra sections ──────────────────────────────────────

export const VISIT_EXTRA_SECTIONS = {
  new_patient: [
    {
      id: 'new_patient_intake',
      label: 'New patient intake',
      items: [
        {
          id: 'np_intake_forms',
          label: 'New patient intake forms complete',
          required: true,
        },
        {
          id: 'np_prior_treatment',
          label: 'Prior treatment history documented',
          sub: 'PT, injections, bracing, prior surgeries for this complaint',
        },
        {
          id: 'np_work_status',
          label: 'Work status / disability noted if relevant',
        },
        {
          id: 'np_attorney',
          label: 'Attorney / legal involvement noted if applicable',
        },
      ],
    },
  ],

  postop_2wk: [
    {
      id: 'postop_prep',
      label: 'Post-op chart prep',
      items: [
        {
          id: 'po_procedure',
          label: 'Procedure and date confirmed in surgical history',
        },
        {
          id: 'po_wound',
          label: 'Wound / dressing status noted from patient',
          required: true,
          sub: 'Drainage, odor, dehiscence, fever — flag to surgeon immediately if present',
        },
        {
          id: 'po_meds',
          label: 'Confirm patient taking post-op medications as prescribed',
          sub: 'Pain meds, antibiotics, DVT prophylaxis if applicable',
        },
        {
          id: 'po_wt_bearing',
          label: 'Weight bearing / activity compliance documented',
        },
        {
          id: 'po_pt_referral',
          label: 'PT referral in place or confirmed',
        },
      ],
    },
  ],

  postop_6wk: [
    {
      id: 'postop_prep',
      label: 'Post-op chart prep',
      items: [
        {
          id: 'po_procedure',
          label: 'Procedure and date confirmed in surgical history',
        },
        {
          id: 'po_pt_progress',
          label: 'PT progress noted',
          sub: 'Ask patient about PT attendance and functional gains',
        },
        {
          id: 'po_rom',
          label: 'ROM measured and documented',
          sub: 'Compare to pre-op baseline if available',
        },
        {
          id: 'po_imaging_ordered',
          label: 'Post-op imaging ordered / completed if applicable',
        },
        {
          id: 'po_work_clearance',
          label: 'Work / activity clearance status noted',
        },
      ],
    },
  ],

  preop: [
    {
      id: 'preop_prep',
      label: 'Pre-op clearance prep',
      items: [
        {
          id: 'preop_clearance_received',
          label: 'Medical / cardiac clearance received and in chart',
          required: true,
        },
        {
          id: 'preop_labs',
          label: 'Pre-op labs resulted and in chart',
        },
        {
          id: 'preop_imaging',
          label: 'Pre-op imaging complete',
        },
        {
          id: 'preop_auth',
          label: 'Prior authorization confirmed active',
          flag: 'warn',
          flagLabel: 'Verify',
        },
        {
          id: 'preop_anesthesia',
          label: 'Anesthesia pre-op screen complete if required',
        },
        {
          id: 'preop_anticoag',
          label: 'Anticoagulation hold plan confirmed if applicable',
        },
        {
          id: 'preop_npo',
          label: 'NPO instructions given to patient',
        },
      ],
    },
  ],

  surgical_consult: [
    {
      id: 'consult_prep',
      label: 'Surgical consult prep',
      items: [
        {
          id: 'sc_imaging',
          label: 'All relevant imaging in chart and viewable',
          required: true,
        },
        {
          id: 'sc_conservative',
          label: 'Conservative treatment history documented',
          sub: 'PT, injections, bracing duration — needed for auth justification',
        },
        {
          id: 'sc_insurance_auth',
          label: 'Insurance pre-auth requirements noted',
        },
        {
          id: 'sc_second_opinion',
          label: 'Prior surgical opinions or notes obtained if applicable',
        },
      ],
    },
  ],
};

// ─── Helper functions ─────────────────────────────────────────────────────────

export function getSectionsForVisitType(visitType) {
  return [...BASE_SECTIONS, ...(VISIT_EXTRA_SECTIONS[visitType] || [])];
}

export function getAllItemIds(visitType) {
  return getSectionsForVisitType(visitType).flatMap(s => (s.items || []).map(i => i.id));
}

export function getRequiredItemIds(visitType) {
  return getSectionsForVisitType(visitType).flatMap(
    s => (s.items || []).filter(i => i.required).map(i => i.id)
  );
}
